import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, Search, Trash2, XCircle } from "lucide-react";
import { useTenantSourceTenants, useSourceTenants, useUpdateSourceTenantMapping } from "@/api/integrations";
import { useUpdateTenant, useDeleteTenant } from "@/api/tenants";
import { DeleteResourceDialog } from "@/pages/devices/modals/delete-resource-dialog";
import { useVantaStatus, useVantaConnect, useVantaDisconnect, useVantaPushNow, useVantaToggleAutoPush } from "@/api/vanta";
import { useDebounce } from "@/hooks/use-debounce";
import { useTenantContext } from "@/hooks/use-tenant-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/stores/auth-store";
import { canManageTenantResources } from "@/lib/roles";
import type { SourceTenant } from "@/api/types";

const sourceLabels: Record<string, string> = {
  crowdstrike: "CrowdStrike",
  atera: "Atera",
  jumpcloud: "JumpCloud",
};

type MappableSource = "crowdstrike" | "jumpcloud";

export function TenantSettingsPage() {
  const { user } = useAuth();
  const { tenantId, tenant } = useTenantContext();
  const { data, isLoading } = useTenantSourceTenants(tenantId);
  const updateMapping = useUpdateSourceTenantMapping();
  const updateTenant = useUpdateTenant(tenantId);
  const deleteTenant = useDeleteTenant(tenantId);
  const navigate = useNavigate();
  const [mapSource, setMapSource] = useState<MappableSource | null>(null);
  const [unmapTarget, setUnmapTarget] = useState<SourceTenant | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isSuperAdmin = user?.role === "super_admin";

  const mappedSources = useMemo(() => {
    const all = data?.data ?? [];
    return all.filter((st) => st.integration_source !== "atera");
  }, [data]);

  const ateraAutoCorrelate = Boolean(tenant?.metadata?.atera_auto_correlate);

  const handleToggleAtera = (checked: boolean) => {
    updateTenant.mutate({
      metadata: { ...tenant?.metadata, atera_auto_correlate: checked },
    });
  };

  const confirmUnmap = () => {
    if (!unmapTarget) return;
    const updatedTenants = unmapTarget.tenants.filter((id) => id !== tenantId);
    updateMapping.mutate(
      { id: unmapTarget.id, tenants: updatedTenants },
      { onSettled: () => setUnmapTarget(null) },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">
        {tenant?.name ? `${tenant.name} — Settings` : "Settings"}
      </h1>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Integration Mappings</CardTitle>
            {canManageTenantResources(user) && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setMapSource("crowdstrike")}>
                  Map CrowdStrike
                </Button>
                <Button size="sm" onClick={() => setMapSource("jumpcloud")}>
                  Map JumpCloud
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {mappedSources.length === 0 ? (
            <p className="text-text-muted">
              No CrowdStrike or JumpCloud sources mapped yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>External ID</TableHead>
                  {canManageTenantResources(user) && <TableHead className="w-24" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappedSources.map((st) => (
                  <TableRow key={st.id}>
                    <TableCell>
                      <Badge variant="accent">
                        {sourceLabels[st.integration_source] || st.integration_source}
                      </Badge>
                    </TableCell>
                    <TableCell>{st.name || "—"}</TableCell>
                    <TableCell className="font-mono text-text-secondary">
                      {st.external_id}
                    </TableCell>
                    {canManageTenantResources(user) && (
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-danger hover:text-danger"
                          onClick={() => setUnmapTarget(st)}
                          disabled={updateMapping.isPending}
                        >
                          Unmap
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Atera Auto-Correlate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <p className="text-sm text-text-secondary">
              When active, Atera devices will automatically match to existing
              devices in this tenant during sync.
            </p>
            <div className="flex items-center gap-3">
              <Switch
                checked={ateraAutoCorrelate}
                onChange={handleToggleAtera}
                disabled={!canManageTenantResources(user) || updateTenant.isPending}
              />
              <span className="text-sm font-medium text-text-primary">
                {ateraAutoCorrelate ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <VantaCard tenantId={tenantId} canManage={canManageTenantResources(user)} />

      {/* Source-specific mapping dialog */}
      {mapSource && (
        <MapSourceDialog
          open
          source={mapSource}
          onClose={() => setMapSource(null)}
          tenantId={tenantId}
        />
      )}

      {isSuperAdmin && (
        <Card>
          <CardHeader><CardTitle className="text-danger">Danger Zone</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Delete this tenant</p>
                <p className="text-sm text-text-muted">Once deleted, this tenant and all its data will no longer be accessible.</p>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}>
                <Trash2 className="mr-1 h-4 w-4" />
                Delete Tenant
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <DeleteResourceDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          deleteTenant.mutate(undefined, {
            onSuccess: () => navigate("/tenants"),
          });
        }}
        isPending={deleteTenant.isPending}
        resourceName="Tenant"
      />

      {/* Unmap confirmation dialog */}
      <Dialog open={!!unmapTarget} onClose={() => setUnmapTarget(null)}>
        <DialogTitle>Confirm Unmap</DialogTitle>
        <p className="text-sm text-text-secondary">
          Are you sure you want to unmap{" "}
          <span className="font-medium text-text-primary">
            {unmapTarget
              ? `${sourceLabels[unmapTarget.integration_source] || unmapTarget.integration_source}: ${unmapTarget.name || unmapTarget.external_id}`
              : ""}
          </span>{" "}
          from this tenant?
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setUnmapTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={confirmUnmap}
            disabled={updateMapping.isPending}
          >
            {updateMapping.isPending ? "Unmapping..." : "Unmap"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function VantaCard({ tenantId, canManage }: { tenantId: string; canManage: boolean }) {
  const { data: vanta, isLoading } = useVantaStatus(tenantId);
  const connect = useVantaConnect(tenantId);
  const disconnect = useVantaDisconnect(tenantId);
  const pushNow = useVantaPushNow(tenantId);
  const toggleAutoPush = useVantaToggleAutoPush(tenantId);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [pushResult, setPushResult] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [autoPushError, setAutoPushError] = useState<string | null>(null);

  const handlePushNow = () => {
    setPushResult(null);
    pushNow.mutate(undefined, {
      onSuccess: (data) => {
        const total = Object.values(data.counts).reduce((a, b) => a + b, 0);
        setPushResult({ type: "success", message: `Successfully pushed ${total} resource(s) to Vanta.` });
      },
      onError: (err) => {
        const apiMsg = (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        setPushResult({ type: "error", message: apiMsg || err.message || "Push failed" });
      },
    });
  };

  const handleToggleAutoPush = (enable: boolean) => {
    setAutoPushError(null);
    setPushResult(null);
    toggleAutoPush.mutate(enable, {
      onError: (err) => {
        const apiMsg = (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        setAutoPushError(apiMsg || err.message || "Validation push failed");
      },
    });
  };

  const isBusy = pushNow.isPending || toggleAutoPush.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vanta Integration</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-10 w-40" />
        ) : (vanta?.connected || vanta?.status === "error") ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              {vanta.status === "error" ? (
                <XCircle className="h-4 w-4 text-danger" />
              ) : (
                <CheckCircle className="h-4 w-4 text-success" />
              )}
              <span className={`font-medium ${vanta.status === "error" ? "text-danger" : "text-success"}`}>
                {vanta.status === "error" ? "Error" : "Connected"}
              </span>
              {vanta.connected_at && (
                <span className="text-text-muted">
                  since {new Date(vanta.connected_at).toLocaleDateString()}
                </span>
              )}
            </div>

            {vanta.last_pushed_at && (
              <p className="text-sm text-text-secondary">
                Last pushed: {new Date(vanta.last_pushed_at).toLocaleString()}
              </p>
            )}

            {vanta.last_push_error && !pushResult && (
              <div className="flex items-start gap-2 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{vanta.last_push_error}</span>
              </div>
            )}

            {canManage && (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <div>
                  <p className="text-sm font-medium">Auto-push devices</p>
                  <p className="text-xs text-text-muted">
                    {toggleAutoPush.isPending && !vanta.auto_push_enabled
                      ? "Validating push to Vanta..."
                      : "Automatically push devices to Vanta every hour"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {toggleAutoPush.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
                  )}
                  <Switch
                    checked={vanta.auto_push_enabled}
                    onChange={handleToggleAutoPush}
                    disabled={isBusy}
                  />
                </div>
              </div>
            )}

            {autoPushError && (
              <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Auto-push validation failed: {autoPushError}</span>
              </div>
            )}

            {canManage && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePushNow}
                    disabled={isBusy}
                  >
                    {pushNow.isPending ? (
                      <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Pushing to Vanta...</>
                    ) : "Push Now"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-danger hover:text-danger"
                    onClick={() => setConfirmDisconnect(true)}
                    disabled={isBusy || disconnect.isPending}
                  >
                    Disconnect
                  </Button>
                </div>

                {pushResult && (
                  <div className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${
                    pushResult.type === "success"
                      ? "border border-green-500/30 bg-green-500/10 text-green-400"
                      : "border border-red-500/30 bg-red-500/10 text-red-400"
                  }`}>
                    {pushResult.type === "success" ? (
                      <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    ) : (
                      <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    )}
                    <span>{pushResult.message}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">
              Connect this tenant's Vanta account to automatically push device inventory
              into Vanta for compliance reporting.
            </p>
            {canManage && (
              <Button
                size="sm"
                onClick={() => connect.mutate()}
                disabled={connect.isPending}
              >
                {connect.isPending ? (
                  <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Redirecting...</>
                ) : "Connect Vanta"}
              </Button>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={confirmDisconnect} onClose={() => setConfirmDisconnect(false)}>
        <DialogTitle>Disconnect Vanta</DialogTitle>
        <p className="text-sm text-text-secondary">
          This will remove the Vanta connection and stop pushing devices. You can reconnect at any time.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setConfirmDisconnect(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              disconnect.mutate();
              setConfirmDisconnect(false);
            }}
            disabled={disconnect.isPending}
          >
            Disconnect
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}

function MapSourceDialog({
  open,
  source,
  onClose,
  tenantId,
}: {
  open: boolean;
  source: MappableSource;
  onClose: () => void;
  tenantId: string;
}) {
  const updateMapping = useUpdateSourceTenantMapping();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);

  const params = useMemo(() => {
    const p: Record<string, string> = {
      "filter[integration_source]": source,
    };
    if (debouncedSearch) p.search = debouncedSearch;
    return p;
  }, [source, debouncedSearch]);

  const { data, isFetching } = useSourceTenants(params);

  const unmapped = useMemo(() => {
    const all = data?.data ?? [];
    return all.filter((st) => !st.tenants.includes(tenantId));
  }, [data, tenantId]);

  const handleMap = (st: SourceTenant) => {
    const updatedTenants = [...st.tenants, tenantId];
    updateMapping.mutate({ id: st.id, tenants: updatedTenants });
  };

  const handleClose = () => {
    setSearch("");
    onClose();
  };

  const isDebouncing = search.trim() !== debouncedSearch;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Map {sourceLabels[source]}</DialogTitle>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <Input
          placeholder={`Search ${sourceLabels[source]} sources...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          autoFocus
        />
        {(isFetching || isDebouncing) && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-text-muted" />
        )}
      </div>

      <div className="mt-3 max-h-72 space-y-1 overflow-y-auto">
        {unmapped.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-muted">
            {search
              ? "No matching sources found."
              : `No unmapped ${sourceLabels[source]} sources available. Run a sync first to discover sources.`}
          </p>
        ) : (
          unmapped.map((st) => (
            <div
              key={st.id}
              className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-surface-hover"
            >
              <div className="min-w-0 flex-1">
                <span className="truncate text-sm font-medium">
                  {st.name || st.external_id}
                </span>
                {st.name && (
                  <p className="mt-0.5 pl-0.5 text-xs text-text-muted font-mono">
                    {st.external_id}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => handleMap(st)}
                disabled={updateMapping.isPending}
                className="ml-3 shrink-0"
              >
                Add
              </Button>
            </div>
          ))
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button variant="outline" onClick={handleClose}>
          Close
        </Button>
      </div>
    </Dialog>
  );
}
